<template>
  <modal
    :showClose="true"
    @close="$emit('rejectExtensionModal')"
    class="email-extension-modal"
    title="campaign extension configuration"
  >
    <h3 slot="header" class="header">campaign extension configuration</h3>
    <p slot="subHeader" class="sub-header">configure your extension and how it will display in your email</p>
    <div slot="body" class="body-wrapper">
      <!-- Button Styling -->
      <div class="column">
        <label>button text</label>
        <input
          type="text"
          class="button-text-input"
          v-model="extensionDraftState.buttonText"
          maxlength="50"
        />
        <span class="form-field-notice">max 50 characters</span>
      </div>

      <div class="column">
        <label class="color-label">button color (ex: #128FF9)
          <div
            :style="{backgroundColor: extensionDraftState.buttonColor}"
            class="h-4 w-4 rounded-sm ml-1"
          />
        </label>
        <input
          type="text"
          class="button-color-input"
          v-model="extensionDraftState.buttonColor"
          maxlength="25"
        />
      </div>

      <!-- Offer Configuration -->
      <div class="column">
        <label>extension type</label>
        <sb-dropdown 
          :items="extensionTypeRows"
          :selected="selectedExtension" 
          v-on:update:selected="changedExtensionType($event)"
          class="extension-type-dropdown w-full"
          data-cy="email-extension-type-select"
        >
          <template #label="{ selected }">
            <div class="extension-type-label">
              {{ selected.label }}
            </div>
          </template>

          <template #item="{ item }">
            <div class="extension-type-item">
              {{ item.label }}
            </div>
          </template>
        </sb-dropdown>
      </div>

      <sb-loading-icon class="column" v-if="extensionFormLoading" />

      <digital-voucher-form
        class="form"
        v-else-if="selectedExtension.label === 'digital voucher'"
        :digitalVoucherDraftState.sync="extensionDraftState.options"
        :campaign="campaign"
      />

      <bonus-points-form
        class="form"
        v-else-if="selectedExtension.label === 'bonus points'"
        :bonusPointsDraftState.sync="extensionDraftState.options"
        :campaign="campaign"
      />

      <instant-points-form
        class="form"
        v-else-if="selectedExtension.label === 'instant points'"
        :instantPointsDraftState.sync="extensionDraftState.options.bonus_value"
      />

      <target-wallet-form
        class="form"
        v-else-if="selectedExtension.label === 'target wallet'"
        :targetWalletDraftState.sync="extensionDraftState.options"
      />

      <div
        class="form"
        v-else-if="selectedExtension.label === 'any link'">
        email extensions are not compatible with any link
      </div>
    </div>
    <div slot="action" class="action-wrapper">
      <button
        class="button larger done-button"
        :disabled="isDoneButtonDisabled"
        @click="clickedDone()"
        data-cy="email-extension-done-button"
      >
        done
      </button>
    </div>
  </modal>
</template>

<script>
import _ from "lodash";
import { campaignRewardAPI } from "../../../services/api/campaignRewardAPI";
import BonusPointsForm from "../extensions/forms/bonus-points-form.vue";
import DigitalVoucherForm from "../extensions/forms/digital-voucher-form.vue";
import InstantPointsForm from "../extensions/forms/instant-points-form.vue";
import Modal from "../../shared/Modal.vue";
import TargetWalletForm from "../extensions/forms/target-wallet-form.vue";

const extensionTypeRows = [
  {label: "digital voucher", type: 1},
  {label: "bonus points", type: 5},
  {label: "instant points", type: 6},
  {label: "target wallet", type: 3}
],
dropdownSelectedLabel = [
  ...extensionTypeRows,
  {label: "select an extension type", type: 0},
  {label: "any link", type: 4},
]

export default {
  props: {
    campaign: Object,
    extension: Object
  },

  name: "campaign-extensions-modal",

  components: {
    BonusPointsForm,
    DigitalVoucherForm,
    InstantPointsForm,
    Modal,
    TargetWalletForm,
  },

  data: () => {
    return {
      error: null,
      extensionDraftState: {
        buttonColor: '',    // how the button will look
        buttonText: '',     // what the button will say
        handlers: {},       // contains handler for bee iframe
        options: {},        // contains reward parameters
        showModal: false,   // if this modal is open
      },
      selectedExtension: null,
      extensionFormLoading: true,
      extensionTypeRows,
      dropdownSelectedLabel,
    }
  },

  created() {
    this.extensionDraftState = _.cloneDeep(this.extension);
    this.selectedExtension = this.dropdownSelectedLabel.find(extension => extension.type === this.campaign.extension_type);
    this.setExtensionOptions();
  },

  methods: {
    async clickedDone() {
      const campaignPayload = { _type: this.selectedExtension.type };
      const extensionName = this.selectedExtension.label

      if(["digital voucher", "bonus points", "instant points"].includes(extensionName)) {
        if(extensionName == "instant points") {
          this.extensionDraftState.options.bonus_type = "add"
          this.extensionDraftState.options.type = "InstantReward"
        }
        await campaignRewardAPI.patchRewards(this.campaign.reward_id, {
          owner_id: this.campaign.id,
          owner_type: 'Campaign',
          reward: this.extensionDraftState.options
        });
      } else if (extensionName == "target wallet") {
        campaignPayload['_type_options'] = this.extensionDraftState.options.target_wallet;
      }
      this.$emit('resolveExtensionModal', this.extensionDraftState, campaignPayload)
    },

    async setExtensionOptions() {
      const extensionName = this.selectedExtension.label
      if(["none", "any link"].includes(extensionName)) {
        return this.extensionFormLoading = false
      } else if (extensionName === "target wallet") {
        this.extensionDraftState.options = {'target_wallet': this.campaign.wallet_target};
      } else {
        if(!this.campaign.reward_id) {
          const { reward } = await campaignRewardAPI.create(this.campaign.id, "Campaign");
          this.extensionDraftState.options = reward;
          this.campaign.reward_id = reward.id;
        } else {
          const { reward } = await campaignRewardAPI.fetch(this.campaign.reward_id, this.campaign.id, 'Campaign');
          this.extensionDraftState.options = reward;

          await this.$store.dispatch('UPDATE_VALUES', { reward });
          await this.$store.dispatch('VALIDATE', ['reward']);
          await this.$store.dispatch('TOUCH_FIELDS', ['reward']);
        }
      }
      this.extensionFormLoading = false
    },

    changedExtensionType(selectedItem) {
      this.extensionFormLoading = true
      this.selectedExtension = selectedItem;
      this.setExtensionOptions();
    },
  },

  computed: {
    isBonusValueEmptyOrLessThan1(){
      return !this.extensionDraftState.options?.bonus_value ||
             this.extensionDraftState.options?.bonus_value < 1;
    },
    isDoneButtonDisabled() {
      const extensionName = this.selectedExtension?.label

      switch(extensionName) {
        case "digital voucher":
          return !this.extensionDraftState.options.item_name || 
                 !this.extensionDraftState.options.expiration_date
        case "target wallet":
          return ![
            'referral',
            'interests',
            'birthday',
            'profile',
            'home',
            'shop online'
          ].includes(this.extensionDraftState.options.target_wallet)
        case "bonus points":
          return this.isBonusValueEmptyOrLessThan1 ||
                 !this.extensionDraftState.options.item_name ||
                 !this.extensionDraftState.options.expiration_date ||
                 !this.extensionDraftState.options.bonus_type ||
                 !this.extensionDraftState.options.min_purchase_amount;
        case "instant points":
          return !this.extensionDraftState.options?.bonus_value ||
                 this.extensionDraftState.options?.bonus_value < 1;
        default:
          return true
      }
    },
  },
}
</script>

<style scoped>
.email-extension-modal >>> a {
  font-family: "AileronLight",sans-serif;
  margin-bottom: 5px;
}

.email-extension-modal >>> .body-wrapper,
.email-extension-modal >>> .form,
.email-extension-modal >>> .column {
  display: flex;
  flex-direction: column;
}

.email-extension-modal >>> .color-label {
  display: flex;
  align-items: center;
}

.email-extension-modal >>> .color-label >>> div {
  height: 16px;
  width: 16px;
  border-radius: 2px;
  margin-left: 5px
}

.email-extension-modal >>> .column {
  margin: 10px auto;
  width: 85%;
  text-align: left;
}

.email-extension-modal >>> .expiration-date {
  border: 1px solid #128ff9;
  border-radius: 8px;
}

.email-extension-modal >>> .expiration-date > .label-container {
  color: #000;
  font-family: "AileronLight";
}

.email-extension-modal >>> .header {
  line-height: 1rem;
  padding-bottom: 0;
  font-size: 2em;
}

.email-extension-modal >>> img {
  border: 1px solid #128ff9;
  height: 75px;
  margin-top: 8px;
  margin-right: auto;
}

.email-extension-modal >>> .modal {
  width: 80%;
}

.email-extension-modal >>> .sb-button {
  margin: 10px;
}

.email-extension-modal >>> span.remove-btn {
  color: #a4a4a4;
  cursor: pointer;
  font-size: .9em;
  margin-left: 15px;
  margin-right: auto;
}

.email-extension-modal >>> .sb-checkbox {
  width: 16px;
  height: 16px;
  border: 1px solid #128ff9;
  margin-right: 15px;
}

.email-extension-modal >>> .sb-vue-dropdown .item,
.email-extension-modal >>> .sb-vue-dropdown .selected {
  font-size: 18px;
  font-family: "AileronLight";
}

.email-extension-modal >>> .sub-header {
  margin: auto;
}

.email-extension-modal >>> svg.fa-camera {
  height: 4rem;
  width: 4rem;
}

.email-extension-modal >>> .tab-container {
  align-items: center;
  display: flex;
  flex-direction: row;
  margin-bottom:10px;
  width: 100%;
}

.email-extension-modal >>> .tab {
  align-items: center;
  border-color: #128fff;
  border-style: solid;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  font-family: 'AileronThin',sans-serif;
  line-height: 1;
  padding: 4px 10px;
  width: 12%;
}

.email-extension-modal >>> .tab span:last-of-type {
  color: #a4a4a4;
  font-size: 12.8px;
  margin-top: 2px;
}

.email-extension-modal >>> .tab.selected {
  background-color: #128fff;
}

.email-extension-modal >>> .tab.selected span {
  color: white;
}

.email-extension-modal >>> .tab:first-child {
  border-bottom-left-radius: 8px;
  border-top-left-radius: 8px;
  border-width: 1px;
}

.email-extension-modal >>> .tab:last-child {
  border-bottom-right-radius: 8px;
  border-top-right-radius: 8px;
  border-width: 1px 1px 1px 0;
}

.email-extension-modal >>> .remove-btn:hover{
  color: #f91253;
}

.email-extension-modal >>> .row {
  cursor: pointer;
  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 21px;
  min-width: 140px;
}

.email-extension-modal >>> .sb-vue-dropdown.focused,
.email-extension-modal >>> .sb-loading-icon.column {
  height: 10rem;
}

.email-extension-modal >>> .error {
  border-color: #f91253;
}

.sb-loading-icon >>> svg {
  fill: #128ff9;
}

.email-extension-modal >>> input,
.email-extension-modal >>> select,
.email-extension-modal >>> .expiration-date {
  margin-bottom: 0;
  width: 100%;
}

.email-extension-modal >>> select,
.email-extension-modal >>> input {
  font-size: 18px;
}

.extension-type-dropdown >>> .extension-type-label,
.extension-type-dropdown >>> .extension-type-item,
.extension-type-dropdown >>> .icon-container > svg {
  color: #128ff9
}

.extension-type-dropdown >>> .label-container {
  background-color: #fff;
}
</style>
